import { Component, computed, inject, linkedSignal, signal } from "@angular/core"
import { NgClass } from "@angular/common"
import { httpResource } from "@angular/common/http"
import { ActivatedRoute, Router } from "@angular/router"
import { toSignal } from "@angular/core/rxjs-interop"
import { firstValueFrom, map } from "rxjs"
import { MatDialog } from "@angular/material/dialog"
import { MatFormField } from "@angular/material/form-field"
import { MatSelect, MatOption } from "@angular/material/select"
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner"
import { FormField, form, submit } from "@angular/forms/signals"
import {
    CATEGORY_FIELD_LABELS,
    Category,
    ModifyCategory,
    ModifyCategoryFactory,
    ModifyObjectFactory,
} from "../../models"
import { CategoryService, ToastService } from "../../services"
import { JsonPatchDocumentGenerator } from "../../helpers/JsonPatchDocumentGenerator"
import { BooleanInputComponent } from "../../components/boolean-input/boolean-input.component"
import {
    ConfirmDialogComponent,
    DictionaryInputComponent,
} from "../../components"
import {
    PATCH_IGNORED_FIELDS,
    describeError,
    invalidFieldsDetail,
    toServerFieldErrors,
} from "../../helpers/FormErrors"

@Component({
    selector: "valben-categories-edit",
    templateUrl: "./categories-edit.page.html",
    styles: [`
        :host {
            width: 100%;
            flex: 1 1 auto;
            min-height: 0;
            display: flex;
            flex-direction: column;
        }
    `],
    standalone: true,
    imports: [
        NgClass,
        MatProgressSpinnerModule,
        MatFormField,
        MatSelect,
        MatOption,
        FormField,
        DictionaryInputComponent,
        BooleanInputComponent,
    ],
})
export class CategoriesEditPage {
    private _categoryService = inject(CategoryService)
    private _messageService = inject(ToastService)
    private _router = inject(Router)
    private _dialog = inject(MatDialog)
    private _patchGenerator = new JsonPatchDocumentGenerator()

    private _masterId = toSignal(inject(ActivatedRoute).paramMap.pipe(map(p => p.get("masterId") ?? "")), {
        initialValue: "",
    })

    category = httpResource<Category>(() => {
        const masterId = this._masterId()
        return masterId ? this._categoryService.ByMasterIdUrl(masterId) : undefined
    })

    allCategories = httpResource<Category[]>(() => this._categoryService.AllUrl())

    saving = signal(false)

    model = linkedSignal<ModifyCategory>(() => {
        const category = this.category.value()
        return category ? ModifyCategoryFactory.from(category) : ModifyCategoryFactory.create()
    })

    categoryForm = form(this.model)

    loading = computed(() => this.category.isLoading() || this.allCategories.isLoading() || this.saving())
    loaded = computed(() => this.category.hasValue())

    typeOptions = [
        { value: "Award", display: "Nagrada" },
        { value: "Perk", display: "Pogodnost" },
        { value: "Benefit", display: "Benefit" },
    ]

    parentOptions = computed(() => {
        const type = this.categoryForm.type().value().toLowerCase()
        const masterId = this._masterId()
        return (this.allCategories.value() ?? [])
            .filter(c => c.type.toLowerCase() === type && c.masterId !== masterId)
            .map(c => ({ masterId: c.masterId, name: c.names?.["hr"] ?? "" }))
    })

    setType(type: string): void {
        this.categoryForm.type().value.set(type)
        const parentId = this.categoryForm.parentId().value()
        if (parentId && !this.parentOptions().some(o => o.masterId === parentId)) {
            this.categoryForm.parentId().value.set("")
        }
    }

    async save(): Promise<void> {
        const original = this.category.value()
        if (!original) return

        this.saving.set(true)
        let failed = false

        const saved = await submit(this.categoryForm, {
            action: async f => {
                const patch = this._patchGenerator.getJsonPatchDocument(
                    ModifyCategoryFactory.from(original),
                    f().value(),
                    PATCH_IGNORED_FIELDS
                )

                try {
                    await firstValueFrom(
                        this._categoryService.Modify(
                            this._masterId(),
                            ModifyObjectFactory.create(patch, original.version)
                        )
                    )
                    return null
                } catch (err) {
                    failed = true
                    const { detail, errors } = toServerFieldErrors(err, this.categoryForm, CATEGORY_FIELD_LABELS)
                    this._error(detail)
                    return errors
                }
            },
            onInvalid: () => this._error(invalidFieldsDetail(this.categoryForm, CATEGORY_FIELD_LABELS)),
        })

        this.saving.set(false)
        if (failed || !saved) return

        this._messageService.add({
            severity: "success",
            summary: "Success",
            detail: "Uspješno izmijenjeno",
            life: 10000,
        })
        this.back()
    }

    async delete(): Promise<void> {
        this.saving.set(true)
        try {
            await firstValueFrom(this._categoryService.Delete(this._masterId()))
            this._messageService.add({
                severity: "success",
                summary: "Success",
                detail: "Uspješno obrisano",
                life: 10000,
            })
            this.back()
        } catch (err) {
            this._error(describeError(err).message)
        } finally {
            this.saving.set(false)
        }
    }

    async showDeleteModal(): Promise<void> {
        const confirmed = await firstValueFrom(
            this._dialog
                .open(ConfirmDialogComponent, {
                    width: "35rem",
                    data: { header: "Jeste li sigurni da želite obrisati entitet?" },
                })
                .afterClosed()
        )
        if (confirmed) await this.delete()
    }

    back(): void {
        this._router.navigate(["/categories"])
    }

    private _error(detail: string): void {
        this._messageService.add({ severity: "error", summary: "Error", detail, life: 10000 })
    }
}
